import { FileSharePermission, SharedPermissionLevel } from '../types';

interface PermissionCopy {
    label: string;
    description: string;
    badgeClass: string;
}

const SHARED_PERMISSION_COPY: Record<SharedPermissionLevel, PermissionCopy> = {
    view: {
        label: 'Alleen bekijken',
        description: 'Je kunt deze opname en transcripties bekijken.',
        badgeClass: 'bg-gray-200 text-gray-700',
    },
    edit: {
        label: 'Bewerken',
        description: 'Je kunt de opname hernoemen en samenvattingen aanpassen, maar niet downloaden.',
        badgeClass: 'bg-blue-100 text-blue-700',
    },
    download: {
        label: 'Download & bewerken',
        description: 'Je kunt downloaden, transcripties opnieuw starten en samenvattingen aanpassen.',
        badgeClass: 'bg-indigo-100 text-indigo-700',
    },
    manage: {
        label: 'Gedeeld',
        description: 'Volledige controle: bewerken, downloaden',
        badgeClass: 'bg-purple-100 text-purple-700',
    },
    owner: {
        label: 'Eigen bestand',
        description: 'Je bent eigenaar en hebt volledige controle.',
        badgeClass: 'bg-green-100 text-green-700',
    },
};

const FILE_SHARE_PERMISSION_COPY: Record<FileSharePermission, PermissionCopy> = {
    view: SHARED_PERMISSION_COPY.view,
    edit: SHARED_PERMISSION_COPY.edit,
    download: SHARED_PERMISSION_COPY.download,
    manage: SHARED_PERMISSION_COPY.manage,
};

export const getSharedPermissionCopy = (permission: SharedPermissionLevel | null | undefined): PermissionCopy | null => {
    if (!permission) {
        return null;
    }

    return SHARED_PERMISSION_COPY[permission];
};

export const getFileSharePermissionCopy = (permission: FileSharePermission): PermissionCopy => {
    return FILE_SHARE_PERMISSION_COPY[permission];
};

export const deriveEffectiveAccess = ({
    canEdit,
    canDownload,
    canManage,
    sharedPermission,
}: {
    canEdit?: boolean;
    canDownload?: boolean;
    canManage?: boolean;
    sharedPermission?: SharedPermissionLevel | null;
}): {
    canEdit: boolean;
    canDownload: boolean;
    canManage: boolean;
    hasExplicitPermissions: boolean;
} => {
    const normalizedShared = sharedPermission ?? null;

    let resolvedCanManage = false;
    if (canManage !== undefined) {
        resolvedCanManage = canManage;
    } else if (normalizedShared === 'manage' || normalizedShared === 'owner') {
        resolvedCanManage = true;
    }

    let resolvedCanDownload = false;
    if (canDownload !== undefined) {
        resolvedCanDownload = canDownload;
    } else if (normalizedShared === 'download' || normalizedShared === 'manage' || normalizedShared === 'owner') {
        resolvedCanDownload = true;
    }

    let resolvedCanEdit = false;
    if (canEdit !== undefined) {
        resolvedCanEdit = canEdit;
    } else if (normalizedShared === 'edit' || normalizedShared === 'download' || normalizedShared === 'manage' || normalizedShared === 'owner') {
        resolvedCanEdit = true;
    }

    if (!resolvedCanEdit && (canDownload || resolvedCanDownload || resolvedCanManage)) {
        resolvedCanEdit = true;
    }

    const hasExplicitPermissions = [canEdit, canDownload, canManage].some(value => value !== undefined) || normalizedShared !== null;

    if (!hasExplicitPermissions) {
        return {
            canEdit: true,
            canDownload: true,
            canManage: true,
            hasExplicitPermissions: false,
        };
    }

    return {
        canEdit: resolvedCanEdit,
        canDownload: resolvedCanDownload,
        canManage: resolvedCanManage,
        hasExplicitPermissions,
    };
};
